import { Injectable } from '@angular/core';
import {
  format,
  parseISO,
  isValid,
  differenceInMilliseconds,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
  add,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfYear,
  endOfDay,
  endOfWeek,
  endOfMonth,
  endOfYear,
  Locale,
} from 'date-fns';
import { fr, enCA } from 'date-fns/locale';
import { toZonedTime, format as formatTz } from 'date-fns-tz';

interface Duration {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
}

export type UnitOfTime =
  | 'milliseconds'
  | 'seconds'
  | 'minutes'
  | 'hours'
  | 'days'
  | 'weeks'
  | 'months'
  | 'years';

export type DurationUnit =
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

export type StartOfUnit = 'day' | 'week' | 'month' | 'year';

export type EndOfUnit = 'day' | 'week' | 'month' | 'year';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  private currentLocale = 'en-US';

  setLocale(locale: string) {
    this.currentLocale = locale;
  }

  private getLocale() {
    const localeMap: { [key: string]: Locale } = {
      'fr-ca': fr,
      'en-ca': enCA,
    };
    return localeMap[this.currentLocale.toLowerCase()] || enCA;
  }

  private parseDate(date: string | Date): Date {
    if (date instanceof Date) return date;

    // Try parseISO first for ISO format dates
    const parsed = parseISO(date);
    if (isValid(parsed)) {
      return parsed;
    }

    // Fall back to native Date constructor for other formats (e.g., M/d/yyyy)
    return new Date(date);
  }

  private getFormatString(momentFormat: string): string {
    // Convert Moment.js format to date-fns format
    const formatMap: { [key: string]: string } = {
      'YYYY-MM-DDTHH:mm:ssZ': "yyyy-MM-dd'T'HH:mm:ssXXX",
      'YYYY-MM-DDTHH:mm:ss': "yyyy-MM-dd'T'HH:mm:ss",
      'YYYY-MM-DDTHH:mm': "yyyy-MM-dd'T'HH:mm",
      'MM/DD/YYYY hh:mm A': 'MM/dd/yyyy hh:mm aa',
      MMDD_HHmm: 'MMddHHmm',
      LL: 'MMMM d, yyyy',
      YYYY: 'yyyy',
      MM: 'MM',
      DD: 'dd',
      HH: 'HH',
      hh: 'hh',
      mm: 'mm',
      ss: 'ss',
      A: 'aa',
    };

    let result = momentFormat;
    Object.entries(formatMap).forEach(([momentToken, dateFnsToken]) => {
      result = result.replace(momentToken, dateFnsToken);
    });
    return result;
  }

  /**
   * Creates a Moment.js date object
   * @param date The date to create
   * @returns The Moment.js date object
   */
  createMoment(date: string | Date): Date {
    return this.parseDate(date);
  }

  /**
   * Formats a date to a local date and time
   * @param date The date to format
   * @param momentFormat The format string to use
   * @returns The formatted date string in local time
   */
  formatLocalDateTime(date: string | Date, momentFormat = 'YYYY-MM-DDTHH:mm:ssZ'): string {
    const localDate = this.parseDate(date);
    return format(localDate, this.getFormatString(momentFormat));
  }

  /**
   * Formats a date to ISO 8601 format (YYYY-MM-DDTHH:mm:ssZ)
   * Similar to moment's format() without parameters
   */
  toISOString(date: string | Date): string {
    return this.formatLocalDateTime(date, 'YYYY-MM-DDTHH:mm:ssZ');
  }

  /**
   * Checks if a date is valid
   * @param date The date to check
   * @returns boolean indicating if the date is valid
   */
  isValid(date: string | Date): boolean {
    try {
      const d = this.parseDate(date);
      return isValid(d);
    } catch {
      return false;
    }
  }

  /**
   * Calculates the difference between two dates
   * @param dateA The first date
   * @param dateB The second date
   * @param unit The unit of time to calculate the difference in
   * @returns The difference between the two dates in the specified unit
   */
  diff(dateA: string | Date, dateB: string | Date, unit: UnitOfTime): number {
    const a = this.parseDate(dateA);
    const b = this.parseDate(dateB);

    const diffFunctions = {
      milliseconds: differenceInMilliseconds,
      seconds: differenceInSeconds,
      minutes: differenceInMinutes,
      hours: differenceInHours,
      days: differenceInDays,
      weeks: differenceInWeeks,
      months: differenceInMonths,
      years: differenceInYears,
    };

    return diffFunctions[unit](a, b);
  }

  /**
   * Adds a duration to a date
   * @param date The date to add the duration to
   * @param amount The amount to add
   * @param unit The unit of time to add
   * @returns The date with the duration added
   */
  add(date: string | Date, amount: number, unit: DurationUnit): Date {
    const d = this.parseDate(date);
    const durationMap: { [key in DurationUnit]: Duration } = {
      millisecond: { milliseconds: amount },
      second: { seconds: amount },
      minute: { minutes: amount },
      hour: { hours: amount },
      day: { days: amount },
      week: { weeks: amount },
      month: { months: amount },
      year: { years: amount },
    };

    return add(d, durationMap[unit]);
  }

  /**
   * Checks if two dates are the same
   * @param dateA The date to check
   * @param dateB The date to compare against
   * @returns boolean indicating if the dates are the same
   */
  isSame(dateA: string | Date, dateB: string | Date): boolean {
    const a = this.parseDate(dateA);
    const b = this.parseDate(dateB);
    return isEqual(a, b);
  }

  /**
   * Checks if a date is the same or after another date
   * @param dateA The date to check
   * @param dateB The date to compare against
   * @returns boolean indicating if the date is the same or after the other date
   */
  isSameOrAfter(dateA: string | Date, dateB: string | Date): boolean {
    const a = this.parseDate(dateA);
    const b = this.parseDate(dateB);
    return isEqual(a, b) || isAfter(a, b);
  }

  /**
   * Checks if a date is the same or before another date
   * @param dateA The date to check
   * @param dateB The date to compare against
   * @returns boolean indicating if the date is the same or before the other date
   */
  isSameOrBefore(dateA: string | Date, dateB: string | Date): boolean {
    const a = this.parseDate(dateA);
    const b = this.parseDate(dateB);
    return isEqual(a, b) || isBefore(a, b);
  }

  /**
   * Formats a date for a file name
   * @param date The date to format
   * @param momentFormat The format string to use
   * @returns The formatted date string for a file name
   */
  formatForFileName(date: Date | string = new Date(), momentFormat = 'MMDD_HHmm'): string {
    const d = this.parseDate(date);
    return format(d, this.getFormatString(momentFormat));
  }

  /**
   * Formats a date in UTC
   * @param date The date to format
   * @param momentFormat The format string to use
   * @returns The formatted date string in UTC
   */
  formatUTC(date: string | Date, momentFormat = 'YYYY-MM-DDTHH:mm:ss'): string {
    const d = this.parseDate(date);
    return format(d, this.getFormatString(momentFormat));
  }

  /**
   * Formats a date in UTC with timezone offset adjustment (for legacy compatibility)
   * Similar to previous Moment.js behavior where selecting a date like April 1 2025
   * would result in March 31 23:00:00 for UTC-1 timezone
   * @param date The date to format
   * @param isEndDate Optional flag to handle end date differently
   * @returns The formatted date string with timezone offset applied in format YYYY-MM-DDTHH:mm:ss
   */
  formatUTCWithOffset(date: string | Date, isEndDate: boolean = false): string {
    if (!date) return '';
    const d = this.parseDate(date);
    // Get timezone offset in minutes
    const tzOffset = d.getTimezoneOffset();
    // Create a new date by subtracting the offset hours
    let adjustedDate = new Date(d.getTime() + tzOffset * 60000);
    // For end date, add one day to match previous behavior
    if (isEndDate) {
      adjustedDate = add(adjustedDate, { days: 1 });
    }
    // Format using the existing formatUTC method
    return this.formatUTC(adjustedDate, 'YYYY-MM-DDTHH:mm:ss');
  }

  /**
   * Formats a date in UTC and adds a duration
   * @param date The date to format
   * @param amount The amount to add
   * @param unit The unit of time to add
   * @param momentFormat The format string to use
   * @returns The formatted date string in UTC
   */
  formatUTCAndAdd(
    date: string | Date,
    amount: number,
    unit: DurationUnit,
    momentFormat = 'YYYY-MM-DDTHH:mm:ss',
  ): string {
    const d = this.add(date, amount, unit);
    return this.formatUTC(d, momentFormat);
  }

  /**
   * Formats a date in UTC to local time
   * @param date The date to format
   * @returns The formatted date string in local time
   */
  formatUTCToLocalTime(date: string | Date): string {
    const localDate = this.parseDate(date);
    return format(localDate, 'MM/dd/yyyy - hh:mm aa');
  }

  /**
   * Converts a date string or Date object to a Date object
   * @param dateStr The date string or Date object to convert
   * @returns The converted Date object
   */
  toDate(dateStr: string | Date): Date {
    if (!dateStr) return null;
    return this.parseDate(dateStr);
  }

  /**
   * Formats a date using date-fns
   * @param date The date to format
   * @param momentFormat The format string to use
   * @returns The formatted date string
   */
  format(date: string | Date, momentFormat: string): string {
    if (!date) return '';
    const d = this.parseDate(date);
    const formatStr = this.getFormatString(momentFormat);
    return format(d, formatStr, { locale: this.getLocale() });
  }

  /**
   * Starts a date at the specified unit
   * @param date The date to start
   * @param unit The unit to start at
   * @returns The date started at the specified unit
   */
  startOf(date: string | Date, unit: StartOfUnit): Date {
    const d = this.parseDate(date);
    const startOfFunctions = {
      day: startOfDay,
      week: startOfWeek,
      month: startOfMonth,
      year: startOfYear,
    };
    return startOfFunctions[unit](d);
  }

  /**
   * Ends a date at the specified unit
   * @param date The date to end
   * @param unit The unit to end at
   * @returns The date ended at the specified unit
   */
  endOf(date: string | Date, unit: EndOfUnit): Date {
    const d = this.parseDate(date);
    const endOfFunctions = {
      day: endOfDay,
      week: endOfWeek,
      month: endOfMonth,
      year: endOfYear,
    };
    return endOfFunctions[unit](d);
  }

  /**
   * Checks if a value is a Date object
   * @param value The value to check
   * @returns boolean indicating if the value is a Date object
   */
  isMoment(value: any): boolean {
    return value instanceof Date;
  }

  /**
   * Converts a date to a specific timezone and formats it
   * @param date The date to convert
   * @param timeZone The timezone to convert to (e.g. 'America/New_York')
   * @param formatStr The format string to use
   * @returns Formatted date string in the specified timezone
   */
  formatInTimeZone(date: Date | string, timeZone: string, momentFormat = 'MMM. d, h:mm A'): string {
    const parsedDate = this.parseDate(date);
    // If the date is already in the target timezone, just format it
    if (parsedDate.toTimeString().includes(timeZone)) {
      return format(parsedDate, this.getFormatString(momentFormat));
    }
    // Otherwise, convert to the target timezone and format
    const zonedDate = toZonedTime(parsedDate, timeZone);
    return formatTz(zonedDate, this.getFormatString(momentFormat), { timeZone });
  }

  /**
   * Converts a date to a specific timezone
   * @param date The date to convert
   * @param timeZone The timezone to convert to
   * @returns Date object in the specified timezone
   */
  convertToTimeZone(date: Date | string, timeZone: string): Date {
    return toZonedTime(this.parseDate(date), timeZone);
  }

  /**
   * Checks if a date is today in a specific timezone
   * @param date The date to check
   * @param timeZone The timezone to check in
   * @returns boolean indicating if the date is today in the specified timezone
   */
  isTodayInTimeZone(date: Date | string, timeZone: string): boolean {
    const zonedDate = this.convertToTimeZone(date, timeZone);
    const zonedToday = this.convertToTimeZone(new Date(), timeZone);
    return this.startOf(zonedDate, 'day').getTime() === this.startOf(zonedToday, 'day').getTime();
  }

  /**
   * Gets the local timezone identifier
   * @returns string The IANA timezone identifier (e.g., 'America/New_York')
   */
  getLocalTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Converts a date to a UTC ISO string (e.g., 2024-06-13T18:00:00.000Z)
   * @param date The date to convert
   * @returns The ISO string in UTC
   */
  toUTCISOString(date: string | Date): string {
    return this.parseDate(date).toISOString();
  }
}
